import { useState } from 'react';
import { systemMessage } from "../systemMessage";
import axios from 'axios';
import { useAuth } from '../AuthContext';
import API_URL from '../config/apiConfig';
import Swal from 'sweetalert2';
import useErrorHandler from './useErrorHandler';

const useSendMessages = (
  inputValue,
  setInputValue,
  setIsLoading,
  setSentImageUrls,
  setUploadedImageUrls,
  uploadedImageUrls,
  messages,
  setMessages,
  currentConversationId,
  setCurrentConversationId,
  refreshHistory,
  pdfStats
) => {
  const authInfo = useAuth();
  const userId = authInfo.authToken?.userId;
  const handleError = useErrorHandler();

  const handleSend = async (event, text) => {
    if (event) event.preventDefault();

    if ((!text || !text.trim()) && (!uploadedImageUrls || uploadedImageUrls.length === 0)) return;

    // Vérification quota avant envoi
    if (pdfStats && pdfStats.remaining !== undefined && pdfStats.remaining <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Quota atteint',
        text: `Vous avez utilisé vos ${pdfStats.total} crédits pour ce mois. Contactez votre administrateur pour augmenter votre quota.`,
        confirmButtonColor: '#1c2143'
      });
      return;
    }

    setIsLoading(true);

    if (!userId) {
      await handleError('Non connecté', 'Veuillez vous reconnecter.', 'handleSend');
      setIsLoading(false);
      return;
    }

    // 1. Message Utilisateur
    const messageContent = [];
    if (text?.trim()) messageContent.push({ type: 'text', text: text.trim() });
    if (Array.isArray(uploadedImageUrls)) {
      uploadedImageUrls.forEach(url => url && messageContent.push({ type: 'image_url', image_url: { url } }));
    }

    const newMessage = { role: 'user', content: messageContent };
    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    setTimeout(() => setUploadedImageUrls([]), 500);

    let activeConvId = currentConversationId;

    try {
      // 2. Sauvegarde User BDD
      const textContent = messageContent.find(m => m.type === 'text')?.text || '';
      const saveResponse = await axios.post(`${API_URL}/messages`, {
        userId,
        text: textContent,
        sender: 'user',
        conversationId: activeConvId
      });

      if (saveResponse.data?.conversationId) {
          activeConvId = saveResponse.data.conversationId;
          if (activeConvId !== currentConversationId && setCurrentConversationId) {
             setCurrentConversationId(activeConvId);
          }
      }

      // 3. Sauvegarde Images (Legacy)
      messageContent.filter(m => m.type === 'image_url').forEach(async (img) => {
         await axios.post(`${API_URL}/images`, { userId, imagePath: img.image_url.url }).catch(console.error);
      });

      // 4. Appel IA
      const payload = {
        model: 'gpt-4o', 
        messages: [systemMessage, ...messages, newMessage], // On envoie l'historique complet
        userId: userId, // ✅ IMPORTANT : On envoie l'ID pour le backend
        conversationId: activeConvId // ✅ IMPORTANT
      };

      const token = localStorage.getItem('auth-token');
      const response = await fetch(`${API_URL}/openai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 401) {
        Swal.fire({
          icon: 'warning',
          title: 'Session expirée',
          text: 'Votre session a expiré. Veuillez vous reconnecter.',
          confirmButtonColor: '#1c2143'
        }).then(() => {
          localStorage.removeItem('auth-token');
          localStorage.removeItem('auth-user');
          window.location.href = '/';
        });
        return;
      }
      if (!response.ok) throw new Error('Erreur OpenAI');

      const data = await response.json();
      const aiContent = data.choices?.[0]?.message?.content || "";
      const assistantMessage = { role: 'assistant', content: aiContent };

      setMessages(prev => [...prev, assistantMessage]);

      // 5. Sauvegarde Bot BDD
      if (activeConvId) {
          await axios.post(`${API_URL}/messages`, {
            userId,
            text: aiContent,
            sender: 'bot',
            conversationId: activeConvId
          });
      }

    } catch (error) {
      await handleError('Erreur', 'Problème lors de l\'échange', 'handleSend', error);
    } finally {
      setIsLoading(false);
      setSentImageUrls(prev => [...prev, ...uploadedImageUrls]);

      // Rafraîchir la sidebar après chaque message
      if (refreshHistory) {
          await refreshHistory();
      }
    }
  };

  return { handleSend };
};

export default useSendMessages;